import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render(container: HTMLElement, options: Record<string, unknown>): string;
      reset(widgetId: string): void;
      remove(widgetId: string): void;
    };
  }
}

/**
 * The widget's site key.
 *
 * **Committed on purpose, and it is not a secret.** A Turnstile site key is
 * public by design — it is rendered into the HTML of every page that shows a
 * widget, so treating it as sensitive buys nothing and costs a configuration
 * step in every environment. The *secret* (`TURNSTILE_SECRET`) is the half that
 * matters, it lives only in the backend's secret store, and it never appears in
 * a `VITE_*` variable because those are inlined into the bundle at build time.
 *
 * Overridable via `VITE_TURNSTILE_SITE_KEY` so a fork, a second widget, or a
 * test can point elsewhere without editing source.
 */
export const TURNSTILE_SITE_KEY: string =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TURNSTILE_SITE_KEY) ||
  '0x4AAAAAAEA2rO7QvW-WaYpj';

/**
 * Attribution marker Cloudflare reads to measure the integration.
 *
 * Set **twice**, and both are needed for different reasons: as `data-action` on
 * the `cf-turnstile` container, which is what the documented implicit-rendering
 * contract specifies, and as `action` in the `render()` options, which is what
 * actually reaches Cloudflare when the widget is created programmatically — as
 * it is here. The previous integration rendered programmatically with no action
 * at all, so the attribute alone would have been decoration.
 *
 * Account-level aggregate telemetry, never per-user. Removing it leaves the
 * integration working and loses only the analytics segmentation.
 */
export const TURNSTILE_ACTION = 'turnstile-spin-v2';

export const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

const SCRIPT_ID = 'cf-turnstile-script';

/** Module-scoped so N widgets on one page share one script load, not N. */
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load.')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Turnstile script failed to load.')));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

export interface TurnstileHandle {
  /**
   * Attach to a bare `<div>`: `<div ref={turnstile.containerRef} />`.
   *
   * A **callback ref**, not a `useRef` object, and that is the fix for a real bug
   * rather than a stylistic choice. With a ref object the render effect has to
   * read `ref.current` and bail when it is null — and its dependency list cannot
   * name `ref.current`, so if the container mounts *later* than the hook (any
   * widget inside a modal, a tab, or a conditional branch) the effect never
   * re-runs and **the widget never appears**. A callback ref makes attachment a
   * state change the effect can depend on.
   *
   * That bug shipped: `apps/landing`'s audit-request modal mounts its form after
   * the hook, so the container was never picked up. Nobody saw it because the
   * site key was unset, so no widget was expected in the first place — the moment
   * a real key arrived, a protected form sat permanently at "Verifying…" with no
   * challenge to complete and no way to submit.
   */
  containerRef: (node: HTMLDivElement | null) => void;
  /** The current token, or null when there isn't one to send. */
  token: string | null;
  /** A message safe to show a user, or null. */
  error: string | null;
  /**
   * Discard the current token and re-challenge.
   *
   * **Call this after every attempt, successful or not.** Tokens are single-use:
   * Cloudflare redeems one at siteverify, so a retry carrying the same token is
   * rejected as `timeout-or-duplicate` — the user sees "verification failed" on
   * a perfectly legitimate second submit, which reads as a broken form.
   */
  reset: () => void;
  /** False when no site key resolved, i.e. verification cannot be attempted. */
  enabled: boolean;
}

/**
 * Render a Turnstile widget and expose its token.
 *
 * `appearance: 'interaction-only'` — the widget stays invisible unless
 * Cloudflare decides a challenge is warranted, so the common case costs the user
 * nothing. The container still occupies its place in the form so the layout does
 * not jump when a challenge does appear.
 *
 * The container is given `class="cf-turnstile"` and
 * `data-action="turnstile-spin-v2"` even though rendering is programmatic: the
 * class is what the ecosystem (and any future implicit render) looks for, and
 * the data attribute is the documented analytics marker.
 */
export function useTurnstile(): TurnstileHandle {
  const siteKey = TURNSTILE_SITE_KEY;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);

  useEffect(() => {
    if (!siteKey || !container) return;
    let cancelled = false;

    // Applied here rather than asked of every caller: a marker that each
    // consumer has to remember is a marker some consumer will forget.
    container.classList.add('cf-turnstile');
    container.setAttribute('data-action', TURNSTILE_ACTION);

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          appearance: 'interaction-only',
          callback: (t: string) => setToken(t),
          'error-callback': () =>
            setError('Verification failed to load. Please refresh and try again.'),
          // A token has a lifetime; when it lapses, drop it rather than letting a
          // submit carry a token siteverify will reject.
          'expired-callback': () => setToken(null),
        });
      })
      .catch(() => setError('Verification failed to load. Please refresh and try again.'));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, container]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { containerRef, token, error, reset, enabled: Boolean(siteKey) };
}
